import React, { useEffect, useState } from "react";
import { Mail, UserPlus, Loader2 } from "lucide-react";
import { fetchContacts } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";

const ROLE_COLORS = {
  rep:    { bg: "bg-emerald-500/15", text: "text-emerald-200", border: "border-emerald-500/30" },
  driver: { bg: "bg-sky-500/15",    text: "text-sky-200",    border: "border-sky-500/30" },
  other:  { bg: "bg-zinc-500/15",   text: "text-zinc-200",   border: "border-zinc-500/30" },
};

/**
 * "Email route" button. Clicking opens a small popover of saved contacts.
 * Selecting a contact opens the device's native mail client via a `mailto:`
 * URL — pre-populated with the recipient, subject AND the message body
 * (Google Maps route link).
 *
 * Zero cost (no SendGrid / no API), works identically on Windows, Mac,
 * iPad, Android, iPhone.
 *
 * Props:
 *   getMessage    — () => string  (called at click-time; receives no args)
 *   getSubject    — () => string  (optional; defaults to "JackTrack route")
 *   label         — button label (default "Email route")
 *   testIdPrefix  — for data-testid scoping
 *   compact       — small icon-only style when true
 */
export function EmailRouteButton({
  getMessage,
  getSubject,
  label = "Email route",
  testIdPrefix = "email",
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState(null);

  useEffect(() => {
    if (!open) return;
    setContacts(null);
    fetchContacts()
      .then(setContacts)
      .catch(() => setContacts([]));
  }, [open]);

  // Only contacts with an email address can receive a route via mailto:
  const emailable = (contacts || []).filter((c) => (c.email || "").trim());

  const buildHref = (c) => {
    const body = getMessage?.() || "";
    const subject = getSubject?.() || "JackTrack route";
    const to = (c.email || "").trim();
    const params = [];
    if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
    if (body) params.push(`body=${encodeURIComponent(body)}`);
    return `mailto:${to}${params.length ? `?${params.join("&")}` : ""}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={`${testIdPrefix}-button`}
          variant="outline"
          size={compact ? "icon" : "sm"}
          className="border-zinc-800 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-100 rounded-md text-xs"
          title="Email this route"
        >
          <Mail className={compact ? "w-3.5 h-3.5" : "w-3.5 h-3.5 mr-1.5"} />
          {!compact && label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 bg-zinc-950 border-zinc-700 text-zinc-100 shadow-xl"
      >
        <div className="px-3 py-2 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center justify-between">
          Send to…
          <Link
            to="/settings#contacts"
            onClick={() => setOpen(false)}
            data-testid={`${testIdPrefix}-manage-contacts`}
            className="text-[10px] text-emerald-300 hover:text-emerald-200 normal-case tracking-normal"
          >
            Manage
          </Link>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {contacts === null ? (
            <div className="px-3 py-6 text-center text-xs text-zinc-500 inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading contacts…
            </div>
          ) : emailable.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-zinc-400 space-y-2">
              <UserPlus className="w-4 h-4 mx-auto text-zinc-600" />
              <div>
                {contacts.length === 0
                  ? "No contacts yet."
                  : "No contacts have an email address yet."}
              </div>
              <Link
                to="/settings#contacts"
                onClick={() => setOpen(false)}
                className="inline-block text-emerald-300 hover:text-emerald-200 text-xs"
              >
                {contacts.length === 0
                  ? "Add reps & drivers in Settings →"
                  : "Add email addresses in Settings →"}
              </Link>
            </div>
          ) : (
            <ul>
              {emailable.map((c) => {
                const c2 = ROLE_COLORS[c.role] || ROLE_COLORS.other;
                return (
                  <li key={c.id}>
                    <a
                      data-testid={`${testIdPrefix}-contact-${c.id}`}
                      href={buildHref(c)}
                      onClick={() => setOpen(false)}
                      className="w-full text-left px-3 py-2 hover:bg-zinc-800 transition-colors flex items-center gap-2"
                    >
                      <span
                        className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${c2.bg} ${c2.text} ${c2.border}`}
                      >
                        {c.role}
                      </span>
                      <span className="text-sm flex-1 min-w-0 truncate text-zinc-100">{c.name}</span>
                      <span className="text-[10px] font-mono text-zinc-500 truncate max-w-[140px]">
                        {c.email}
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default EmailRouteButton;
